import { useCallback, useEffect, useMemo, useRef } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { Tooltip, TooltipTrigger } from '@/lib/base/tooltip/tooltip';

import {
  grantListGridClass,
  grantRowGridClass,
} from './api-access-grant-layout';
import type { ScopeKind, WriteKind } from './api-access-grant-row';
import { GrantRow, isPrivateOnlyScoping } from './api-access-grant-row';
import type {
  CredentialReach,
  GrantableGroup,
  GrantableType,
  GrantSelection,
} from './api-access-service';
import {
  GRANTABLE_GROUP_LABEL,
  GRANTABLE_GROUP_ORDER,
} from './api-access-service';
import type { GrantScopings } from './use-grant-scopings';

interface ApiAccessGrantPickerProps {
  value: GrantSelection[];
  onChange: (next: GrantSelection[]) => void;
  /**
   * Resolved grantable types and their read scopings, owned by the submitting
   * parent (see `useGrantScopings`). The picker renders from them rather than
   * resolving its own copy, so the toggles and the submit path can never drift.
   */
  scopings: GrantScopings;
  /**
   * Shows the per-type "Can write" toggle, driving `GrantSelection.includeWrite`.
   * Service accounts are read-only by default; the parent passes `true` only
   * where write grants are meaningful. Defaults to `false`.
   */
  showWriteColumn?: boolean;
  /**
   * What "private" means on every row. `user` (default): the creator's own
   * private records. `org`: every user's private records — the same toggle,
   * a much bigger grant, so the rows say so.
   */
  reach?: CredentialReach;
}

/** Column header hint: what the "Records" column controls. */
const RECORDS_HEADER_HINT = 'What this service account can see for each type.';
/** Column header hint: what the "Can write" column controls. */
const CAN_WRITE_HEADER_HINT =
  'Lets the service account change this data, not just read it.';

/**
 * The single helper line under the "Data access" heading. One sentence per
 * kind/reach combination, so the whole explanation of what the rows mean sits
 * in one place instead of a banner plus two paragraphs.
 */
function pickerHelperText(
  showWriteColumn: boolean,
  reach: CredentialReach
): string {
  if (!showWriteColumn) {
    return 'Read-only: this service account can view what you pick, never change it. Include private adds your own private records.';
  }
  if (reach === 'org') {
    return 'Pick what this service account can read.';
  }
  return 'Pick what this service account can read; Can write lets it change that data. Include private adds your own private records.';
}

export function ApiAccessGrantPicker({
  value,
  onChange,
  scopings,
  showWriteColumn = false,
  reach = 'user',
}: ApiAccessGrantPickerProps) {
  const {
    grantable,
    scopingsByType,
    allowedWriteEventsByType,
    resolveImplied,
    isLoading,
    error: scopingsError,
  } = scopings;

  const impliedLabels = useMemo(
    () => resolveImplied(value.map(s => s.entityTypeName)).labels,
    [resolveImplied, value]
  );

  const selectedTypeNames = useMemo(
    () => new Set(value.map(s => s.entityTypeName)),
    [value]
  );

  const grantableTypeNames = useMemo(
    () => new Set(grantable.map(g => g.entityTypeName)),
    [grantable]
  );

  // Select everything the first time the grantable set resolves, so a credential
  // can be created in one click. Fires ONCE per mount, tracked by a ref rather
  // than by "is the selection empty" — the latter would re-tick everything the
  // moment a user deliberately unticked their last type, making the list
  // impossible to clear.
  const hasAppliedDefaultSelection = useRef(false);
  useEffect(() => {
    if (
      isLoading ||
      hasAppliedDefaultSelection.current ||
      grantable.length === 0
    ) {
      return;
    }
    hasAppliedDefaultSelection.current = true;
    if (value.length > 0) {
      return; // Parent supplied a selection; never overwrite it.
    }
    onChange(
      grantable.map(({ entityTypeName }) => {
        const typeScopings = scopingsByType.get(entityTypeName);
        const privateOnly =
          typeScopings !== undefined && isPrivateOnlyScoping(typeScopings);
        return {
          entityTypeName,
          includePrivate: privateOnly,
          includeWrite: false,
        };
      })
    );
  }, [isLoading, grantable, scopingsByType, value.length, onChange]);

  // Reconcile the selection against the currently grantable types. If a selected
  // type stops being grantable after the fact (permissions or schema refreshed),
  // its checkbox no longer renders — so the user can't uncheck it, yet it would
  // still be submitted and fail with "No read access resolved". Prune those stale
  // entries. Guarded on `!isLoading` so a mid-fetch empty `grantable` never nukes
  // valid selections, and it only calls `onChange` when the set actually shrank,
  // so it converges in one pass instead of looping.
  useEffect(() => {
    if (isLoading) {
      return;
    }
    const pruned = value.filter(s => grantableTypeNames.has(s.entityTypeName));
    if (pruned.length !== value.length) {
      onChange(pruned);
    }
  }, [value, grantableTypeNames, isLoading, onChange]);

  // Classify every grantable type once, so each row can render its own scope
  // control. The Public/Private choice is only meaningful for types that support
  // BOTH sides — an unrestricted type has no split at all — which mirrors
  // FileDefaultsPanel's `hasVisibility` rule.
  const scopeKindByType = useMemo(() => {
    const kinds = new Map<string, ScopeKind>();
    for (const { entityTypeName } of grantable) {
      const typeScopings = scopingsByType.get(entityTypeName);
      if (typeScopings === undefined) {
        kinds.set(entityTypeName, 'unresolved');
      } else if (typeScopings.unrestricted) {
        kinds.set(entityTypeName, 'unrestricted');
      } else if (isPrivateOnlyScoping(typeScopings)) {
        kinds.set(entityTypeName, 'private-only');
      } else if (typeScopings.privateScopings.length === 0) {
        kinds.set(entityTypeName, 'public-only');
      } else {
        kinds.set(entityTypeName, 'togglable');
      }
    }
    return kinds;
  }, [grantable, scopingsByType]);

  // What a write grant on each type can reach. `none` when the schema accepts
  // no write event from a credential on the type, or the credential property
  // excludes it; `links-only` when only the association events are accepted
  // (synced records that may still be linked to other records).
  const writeKindByType = useMemo(() => {
    const kinds = new Map<string, WriteKind>();
    for (const { entityTypeName } of grantable) {
      const allowed = allowedWriteEventsByType.get(entityTypeName) ?? [];
      if (allowed.length === 0) {
        kinds.set(entityTypeName, 'none');
      } else if (allowed.every(e => e.startsWith('ahoy_association_'))) {
        kinds.set(entityTypeName, 'links-only');
      } else {
        kinds.set(entityTypeName, 'full');
      }
    }
    return kinds;
  }, [grantable, allowedWriteEventsByType]);

  const includePrivateByType = useMemo(
    () => new Map(value.map(s => [s.entityTypeName, s.includePrivate])),
    [value]
  );

  const includeWriteByType = useMemo(
    () => new Map(value.map(s => [s.entityTypeName, s.includeWrite ?? false])),
    [value]
  );

  const handleCheckboxToggle = useCallback(
    (entityTypeName: string, isSelected: boolean) => {
      if (!isSelected) {
        onChange(value.filter(s => s.entityTypeName !== entityTypeName));
        return;
      }
      const scopings = scopingsByType.get(entityTypeName);
      const existing = value.find(s => s.entityTypeName === entityTypeName);
      const privateOnly =
        scopings !== undefined && isPrivateOnlyScoping(scopings);
      const newSelection: GrantSelection = {
        entityTypeName,
        includePrivate: privateOnly || (existing?.includePrivate ?? false),
        includeWrite: existing?.includeWrite ?? false,
      };
      onChange([
        ...value.filter(s => s.entityTypeName !== entityTypeName),
        newSelection,
      ]);
    },
    [value, onChange, scopingsByType]
  );

  const handleIncludePrivateChange = useCallback(
    (entityTypeName: string, includePrivate: boolean) => {
      onChange(
        value.map(s =>
          s.entityTypeName === entityTypeName ? { ...s, includePrivate } : s
        )
      );
    },
    [value, onChange]
  );

  const handleIncludeWriteChange = useCallback(
    (entityTypeName: string, includeWrite: boolean) => {
      onChange(
        value.map(s =>
          s.entityTypeName === entityTypeName ? { ...s, includeWrite } : s
        )
      );
    },
    [value, onChange]
  );

  // Counted against the grantable list, not `value`: a stale selection that
  // the reconcile effect is about to prune must not read as a ticked row.
  const selectedCount = useMemo(
    () => grantable.filter(g => selectedTypeNames.has(g.entityTypeName)).length,
    [grantable, selectedTypeNames]
  );

  // Ticks every grantable type, keeping the scope/write choices already made
  // on the rows that were selected — the same shape as the initial default.
  const handleSelectAll = useCallback(() => {
    const existing = new Map(value.map(s => [s.entityTypeName, s]));
    onChange(
      grantable.map(({ entityTypeName }) => {
        const prior = existing.get(entityTypeName);
        if (prior) {
          return prior;
        }
        const typeScopings = scopingsByType.get(entityTypeName);
        return {
          entityTypeName,
          includePrivate:
            typeScopings !== undefined && isPrivateOnlyScoping(typeScopings),
          includeWrite: false,
        };
      })
    );
  }, [grantable, value, onChange, scopingsByType]);

  const handleClearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  // Rows grouped by ontology kind, in a fixed order, so ten-plus types scan
  // as "people, deals, activity" instead of one flat run. Schema order is
  // kept within a group.
  const groupedRows = useMemo(() => {
    const byGroup = new Map<GrantableGroup, GrantableType[]>();
    for (const type of grantable) {
      const rows = byGroup.get(type.group);
      if (rows) {
        rows.push(type);
      } else {
        byGroup.set(type.group, [type]);
      }
    }
    return GRANTABLE_GROUP_ORDER.flatMap(group => {
      const rows = byGroup.get(group);
      return rows ? [{ group, rows }] : [];
    });
  }, [grantable]);
  // A single group needs no heading — the list IS the group.
  const showGroupHeadings = groupedRows.length > 1;

  // Show the loader while any underlying fetch is still in flight. `isLoading`
  // (from `useGrantScopings`) is react-query's first-load signal — true only
  // before ANY data exists, so a background refetch over cached data does NOT
  // trip it (that's the data-first path below). Gating here — rather than only
  // when `grantable` is empty — also covers the window where object types have
  // resolved but association types have not: scopings would resolve empty, so we
  // must not let the user pick scoped types and hit a submit-time failure.
  if (isLoading) {
    return (
      <div className='flex justify-center py-4'>
        <LoadingIndicator size='sm' label='Loading data types…' />
      </div>
    );
  }

  // Data-first: with grantable types in hand, render them even if a background
  // refetch is erroring. Distinguish error from a genuine empty result only when
  // there is nothing to show, so the picker never flashes a false "nothing
  // available" message mid-fetch.
  if (grantable.length === 0) {
    if (scopingsError) {
      return (
        <p className='text-sm text-[var(--color-text-error-primary)]'>
          Unable to load shareable data types. Please try again.
        </p>
      );
    }
    return (
      <p className='text-sm text-[var(--color-text-secondary)]'>
        No shareable data types available for your account.
      </p>
    );
  }

  return (
    <div className='flex flex-col gap-3'>
      {/* One heading and one helper line for the whole list rather than
          per-row prose. The helper bounds everything below it: for kinds
          without the write column, the ahoy_permissions field type sets
          allow_write: false, so a write-shaped grant is rejected at validation
          and the resolver returns an empty write map — saying so stops the
          checkboxes reading as full access. Org reach gets its one caution
          here, next to the toggle it changes, instead of a banner above the
          form. The per-type "why is this read-only" detail lives on the row. */}
      <div className='flex items-center justify-between gap-3'>
        <span className='text-sm font-medium text-fg-secondary'>
          Data access
        </span>
        {/* Bulk controls: everything starts ticked, so "Clear" is how a
            narrow key gets built, and the count keeps the true breadth of
            the grant on screen. */}
        <div className='flex items-center gap-2'>
          {/* The primary action is disabled with nothing ticked; say why
              here, next to the control that fixes it, rather than leaving
              a silently dead button in the footer. */}
          {selectedCount === 0 ? (
            <span className='text-xs text-[var(--color-text-warning-primary)]'>
              Pick at least one type
            </span>
          ) : (
            <span className='text-xs text-fg-tertiary'>
              {selectedCount} of {grantable.length}
            </span>
          )}
          <Button
            size='sm'
            color='link-color'
            onClick={handleSelectAll}
            isDisabled={selectedCount === grantable.length}
          >
            Select all
          </Button>
          <Button
            size='sm'
            color='link-gray'
            onClick={handleClearAll}
            isDisabled={selectedCount === 0}
          >
            Clear
          </Button>
        </div>
      </div>
      <p className='text-sm text-[var(--color-text-secondary)]'>
        {pickerHelperText(showWriteColumn, reach)}
      </p>
      <div
        className={`${grantListGridClass(showWriteColumn)} divide-y divide-[var(--color-border-secondary)] rounded-lg border border-[var(--color-border-primary)]`}
      >
        {/* Column headers carry the words the rows used to repeat. A subgrid
            row like GrantRow, so they sit over their cells. */}
        <div
          className={`${grantRowGridClass(showWriteColumn)} px-4 py-2 text-xs font-medium text-fg-tertiary`}
        >
          <span>Data type</span>
          <span className='text-right'>
            <Tooltip title={RECORDS_HEADER_HINT}>
              <TooltipTrigger className='cursor-default text-fg-tertiary'>
                Records
              </TooltipTrigger>
            </Tooltip>
          </span>
          {showWriteColumn && (
            <span className='text-right'>
              <Tooltip title={CAN_WRITE_HEADER_HINT}>
                <TooltipTrigger className='cursor-default text-fg-tertiary'>
                  Can write
                </TooltipTrigger>
              </Tooltip>
            </span>
          )}
        </div>
        {groupedRows.map(({ group, rows }) => (
          <div
            key={group}
            role='group'
            aria-label={GRANTABLE_GROUP_LABEL[group]}
            className={`${grantRowGridClass(showWriteColumn)} divide-y divide-[var(--color-border-secondary)]`}
          >
            {showGroupHeadings && (
              <div className='col-span-full bg-[var(--color-bg-secondary)] px-4 py-1.5 text-xs font-medium uppercase tracking-wide text-fg-tertiary'>
                {GRANTABLE_GROUP_LABEL[group]}
              </div>
            )}
            {rows.map(({ entityTypeName, label }) => (
              <GrantRow
                key={entityTypeName}
                entityTypeName={entityTypeName}
                label={label}
                isSelected={selectedTypeNames.has(entityTypeName)}
                scopeKind={scopeKindByType.get(entityTypeName) ?? 'unresolved'}
                includePrivate={
                  includePrivateByType.get(entityTypeName) ?? false
                }
                showWriteColumn={showWriteColumn}
                writeKind={writeKindByType.get(entityTypeName) ?? 'none'}
                includeWrite={includeWriteByType.get(entityTypeName) ?? false}
                reach={reach}
                onToggle={handleCheckboxToggle}
                onIncludePrivateChange={handleIncludePrivateChange}
                onIncludeWriteChange={handleIncludeWriteChange}
              />
            ))}
          </div>
        ))}
      </div>
      {impliedLabels.length > 0 && (
        // Shown, not applied silently: these are real read grants, and one of
        // them (a pipeline, say) can carry data the admin did not consciously
        // tick. Surfacing that keeps the credential's true scope on screen —
        // as a footnote under the list, where it reads as a consequence of
        // the choice above rather than as a second helper paragraph.
        <p className='text-xs text-fg-tertiary'>
          Supporting data the types above depend on is included automatically.
        </p>
      )}
    </div>
  );
}
